import { useRef, useState } from 'react';

import { ActionIcon, Group, Stack, useMantineTheme } from '@mantine/core';

import { IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../../../../../../shared/components/UI/Button/Button';
import { TextArea } from '../../../../../../../../../shared/components/UI/TextArea/TextArea';

import { GradientBorder } from '../../../../../../../../../shared/components/GradientBorder';

type MessageInputProps = {
  onSubmit: (content: string) => void;
  isDisabled?: boolean;
};

export function MessageInput({
  onSubmit,
  isDisabled = false,
}: MessageInputProps) {
  const { t } = useTranslation(['bidResponse']);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [message, setMessage] = useState('');
  const theme = useMantineTheme();

  const focusInput = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    textAreaRef.current?.focus();
  };

  const trimmedMessage = message.trim();

  const handleSubmit = () => {
    if (!trimmedMessage) {
      return;
    }

    onSubmit(trimmedMessage);
    setMessage('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && trimmedMessage) {
      e.stopPropagation();
      handleSubmit();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!e.target.value.trim() && !trimmedMessage) {
      return;
    }

    setMessage(e.target.value);
  };

  const handleReset = () => {
    setMessage('');
  };

  return (
    <GradientBorder
      borderWidth={1.6}
      borderRadius={theme.radius.lg}
      gradient={theme.other.gradients.background1}
      wrapperProps={{
        w: '100%',
        maw: '926px',
        sx: {
          boxShadow: '0px 67.5px 101.25px 0px rgba(25, 64, 154, 0.06)',
        },
      }}
      contentProps={{
        w: '100%',
      }}
    >
      <Stack w="100%" spacing={0} p="04" pl="04" pt="03" onClick={focusInput}>
        <TextArea
          ref={textAreaRef}
          autoFocus
          variant="white"
          size="xs"
          fw={400}
          fs="18px"
          maxRows={10}
          disabled={isDisabled}
          placeholder={t('projectDetail.assistant.messageInput.placeholder')}
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          rightSection={
            !!message && (
              <ActionIcon
                variant="transparent"
                size="xs"
                c="primary.6"
                onClick={handleReset}
              >
                <IconX />
              </ActionIcon>
            )
          }
        />
        <ActionRow message={message} onSubmit={handleSubmit} />
      </Stack>
    </GradientBorder>
  );
}

function ActionRow({
  message,
  onSubmit,
}: {
  message: string;
  onSubmit: () => void;
}) {
  const { t } = useTranslation(['bidResponse']);

  return (
    <Group noWrap position="right" pl="01">
      <Button size="md" disabled={!message.trim()} onClick={onSubmit}>
        {t('assistant.messageInput.cta')}
      </Button>
    </Group>
  );
}
